import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "@environments/environment";
import { PlatformConfig } from "@core/models/platform-config.model";
import { SocialPlatform } from "@core/models/social-post.model";

@Injectable({ providedIn: "root" })
export class PlatformConfigService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/platforms`;

  /** All platforms from API (sorted by displayOrder) */
  platforms = signal<PlatformConfig[]>([]);

  /** Active frontend defaults from API (includes wordpress) */
  defaultPlatforms = signal<PlatformConfig[]>([]);

  /** Single active default platform for frontend */
  defaultPlatform = signal<PlatformConfig | null>(null);

  /** Active social platform keys only (excludes wordpress) */
  activeSocialPlatforms = signal<SocialPlatform[]>([]);

  /** Active social platform keys that frontend should preselect */
  defaultSocialPlatforms = signal<SocialPlatform[]>([]);

  /** All social platform keys including inactive (excludes wordpress) */
  allSocialPlatforms = signal<SocialPlatform[]>([]);

  /** Set of inactive platform keys for quick lookup */
  inactivePlatforms = signal<Set<string>>(new Set());

  loaded = signal(false);

  private loadPromise: Promise<void> | null = null;

  /** Load active platforms from API. Safe to call multiple times — only fetches once. */
  load(): Promise<void> {
    if (this.loaded()) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<void>((resolve) => {
      this.http
        .get<{ success: boolean; data: PlatformConfig[] }>(this.apiUrl)
        .subscribe({
          next: (res) => {
            const all = (res.data || []).sort(
              (a, b) => a.displayOrder - b.displayOrder,
            );
            this.platforms.set(all);

            const activeDefaults = all.filter((p) => p.isActive && p.isDefault);
            const singleDefault = activeDefaults[0] || null;
            this.defaultPlatforms.set(singleDefault ? [singleDefault] : []);
            this.defaultPlatform.set(singleDefault);

            const socialAll = all.filter((p) => p.platformKey !== "wordpress");
            const socialDefaults = (
              singleDefault ? [singleDefault] : []
            ).filter((p) => p.platformKey !== "wordpress");
            this.allSocialPlatforms.set(
              socialAll.map((p) => p.platformKey as SocialPlatform),
            );
            this.activeSocialPlatforms.set(
              socialAll
                .filter((p) => p.isActive)
                .map((p) => p.platformKey as SocialPlatform),
            );
            this.defaultSocialPlatforms.set(
              socialDefaults.map((p) => p.platformKey as SocialPlatform),
            );
            this.inactivePlatforms.set(
              new Set(all.filter((p) => !p.isActive).map((p) => p.platformKey)),
            );

            this.loaded.set(true);
            resolve();
          },
          error: () => {
            // On error, keep empty — components fall back gracefully
            this.loaded.set(true);
            resolve();
          },
        });
    });

    return this.loadPromise;
  }

  getPreferredSocialPlatforms(
    fallbackPlatforms: SocialPlatform[] = [],
  ): SocialPlatform[] {
    const defaults = this.defaultSocialPlatforms();
    if (defaults.length > 0) {
      return defaults;
    }

    if (fallbackPlatforms.length > 0) {
      const active = this.activeSocialPlatforms();
      const activeFallbacks = fallbackPlatforms.filter((platform) =>
        active.includes(platform),
      );
      if (activeFallbacks.length > 0) {
        return activeFallbacks;
      }
    }

    const firstActive = this.activeSocialPlatforms()[0];
    return firstActive ? [firstActive] : [];
  }

  getPreferredPlatforms(): SocialPlatform[] {
    const preferred = this.defaultPlatform();
    if (preferred && preferred.isActive) {
      return [preferred.platformKey as SocialPlatform];
    }

    const wordpress = this.platforms().find(
      (platform) => platform.platformKey === "wordpress" && platform.isActive,
    );
    if (wordpress) {
      return ["wordpress"];
    }

    return this.getPreferredSocialPlatforms();
  }
}
